<template>
  <div class="sm_overlay" v-if="internalVisible" @click.self="close">
    <div class="sm_box">
      <!-- header -->
      <div class="sm_header">
        <div class="sm_title">Temporadas &amp; Prêmios</div>
        <div class="sm_close" @click="close"><b-icon-x-lg class="close_icon" /></div>
      </div>

      <div class="sm_body">
        <!-- COL 1: seasons list + create -->
        <div class="sm_col sm_col_seasons">
          <div class="sm_col_title">Temporadas</div>
          <div class="sm_list custom_scroll">
            <div
              v-for="s in seasons"
              :key="s.id"
              class="sm_season"
              :class="{ active: selected && selected.id === s.id }"
              @click="selectSeason(s)"
            >
              <div class="sm_season_head">
                <div class="sm_season_title">{{ s.title }}</div>
                <span class="sm_badge" :class="statusClass(s)">{{ statusLabel(s) }}</span>
              </div>
              <div class="sm_season_sub">
                <b-icon-clock class="sm_season_ico" />
                <span>{{ endLabel(s) }} {{ fmtDate(s.end_at) }}</span>
              </div>
            </div>
            <div v-if="!seasons.length && !loadingSeasons" class="sm_empty">nenhuma temporada</div>
          </div>
          <div class="sm_form">
            <div class="sm_form_title">Nova temporada</div>
            <input class="sm_input" v-model="newSeason.title" placeholder="Título" />
            <label class="sm_field_label">Início (opcional)</label>
            <input class="sm_input sm_dt" type="datetime-local" step="1" v-model="newSeason.start_at" />
            <label class="sm_field_label">Fim</label>
            <input class="sm_input sm_dt" type="datetime-local" step="1" v-model="newSeason.end_at" />
            <CustomButton :name="'Criar temporada'" :loading="creating" @ontap="createSeason" />
          </div>
        </div>

        <!-- COL 2: prizes -->
        <div class="sm_col sm_col_prizes">
          <div class="sm_col_title">
            Prêmios
            <span v-if="selected" class="sm_col_sub">— {{ selected.title }}</span>
          </div>
          <template v-if="selected">
            <div class="sm_list custom_scroll">
              <div
                v-for="p in prizes"
                :key="p.position"
                class="sm_prize"
                :style="{ '--pc': p.color || '#29ff7e' }"
              >
                <div class="sm_prize_bar"></div>
                <div class="sm_prize_pos">{{ p.position }}</div>
                <div class="sm_prize_info">
                  <div class="sm_prize_name">{{ p.prize }}</div>
                  <div class="sm_prize_content" v-if="p.type === 'gift_card' && p.content">
                    <b-icon-gift-fill class="sm_prize_ico" />
                    <span>{{ p.content }}</span>
                  </div>
                  <div
                    v-if="p.type === 'gift_card' && p.winner_user_id"
                    class="sm_delivery"
                    :class="'dlv_' + (p.delivery_status || 'none')"
                  >
                    <template v-if="p.delivery_status === 'delivered'">
                      <b-icon-check-circle-fill /> entregue
                    </template>
                    <template v-else-if="p.delivery_status === 'failed'">
                      <b-icon-exclamation-triangle-fill /> falha na entrega
                      <button
                        type="button"
                        class="sm_redeliver_btn"
                        :disabled="redelivering === p.position"
                        @click="redeliverPrize(p)"
                      >
                        <b-icon-arrow-repeat :class="{ spin: redelivering === p.position }" />
                        {{ redelivering === p.position ? "reenviando…" : "reentregar" }}
                      </button>
                    </template>
                    <template v-else>
                      <b-icon-hourglass-split /> pendente
                    </template>
                  </div>
                  <!-- código de assinatura: gerado sozinho na finalização (subscription_codes) -->
                  <div class="sm_prize_content" v-if="p.type === 'subscription' && p.subscription_code">
                    <b-icon-key-fill class="sm_prize_ico" />
                    <span>{{ p.subscription_code }}</span>
                  </div>
                  <div
                    v-if="p.type === 'subscription' && p.subscription_code"
                    class="sm_delivery"
                    :class="
                      'dlv_' +
                      (p.subscription_code_status === 'active'
                        ? 'delivered'
                        : p.subscription_code_status === 'expired'
                          ? 'failed'
                          : 'none')
                    "
                  >
                    <template v-if="p.subscription_code_status === 'active'">
                      <b-icon-check-circle-fill /> resgatado
                    </template>
                    <template v-else-if="p.subscription_code_status === 'expired'">
                      <b-icon-exclamation-triangle-fill /> expirado
                    </template>
                    <template v-else>
                      <b-icon-hourglass-split /> aguardando resgate
                    </template>
                  </div>
                </div>
                <div class="sm_prize_edit" @click="editPrize(p)"><b-icon-pen /></div>
              </div>
              <div v-if="!prizes.length && !loadingPrizes" class="sm_empty">nenhum prêmio definido</div>
            </div>
            <div class="sm_form">
              <div class="sm_form_title">Definir / editar prêmio</div>
              <!-- tipo: gift card | assinatura (texto, no topo) -->
              <div class="sm_seg sm_seg_full">
                <button
                  type="button"
                  class="sm_seg_btn"
                  :class="{ active: prizeForm.type === 'gift_card' }"
                  @click="setPrizeType('gift_card')"
                >
                  Gift Card
                </button>
                <button
                  type="button"
                  class="sm_seg_btn"
                  :class="{ active: prizeForm.type === 'subscription' }"
                  @click="setPrizeType('subscription')"
                >
                  Assinatura
                </button>
              </div>
              <input class="sm_input" v-model.number="prizeForm.position" type="number" min="1" placeholder="Posição (1, 2, 3…)" />

              <!-- gift card: valor base em USD -->
              <select
                v-if="prizeForm.type === 'gift_card'"
                class="sm_input sm_dt"
                v-model="prizeForm.value"
              >
                <option value="5_usd">Gift Card — $5 USD</option>
                <option value="10_usd">Gift Card — $10 USD</option>
              </select>

              <!-- assinatura: tier (o código é gerado sozinho na finalização) -->
              <template v-if="prizeForm.type === 'subscription'">
                <select class="sm_input sm_dt" v-model="prizeForm.value">
                  <option value="premium_sub">PREMIUM SUB</option>
                  <option value="plus_sub">PLUS SUB</option>
                  <option value="pro_sub">PRO SUB</option>
                </select>
              </template>

              <div class="sm_color_row">
                <span class="sm_color_label">Cor tema</span>
                <input class="sm_color" type="color" v-model="prizeForm.color" />
                <input
                  class="sm_input sm_color_hex_input"
                  v-model="prizeForm.color"
                  placeholder="#RRGGBB"
                  maxlength="9"
                />
              </div>
              <CustomButton :name="'Salvar prêmio'" :loading="savingPrize" @ontap="savePrize" />
            </div>
            <div class="sm_finalize">
              <CustomButton
                :name="selected.status === 'ended' ? 'Temporada encerrada' : 'Finalizar agora'"
                :active="selected.status !== 'ended'"
                :loading="finalizing"
                @ontap="finalize"
              />
            </div>
          </template>
          <div v-else class="sm_empty sm_center">selecione uma temporada</div>
        </div>

        <!-- COL 3: ranking -->
        <div class="sm_col sm_col_ranking">
          <div class="sm_col_title sm_col_title_row">
            <span>Ranking</span>
            <span v-if="selected && rankingTotal" class="sm_col_count">
              {{ rankingTotal }} participante{{ rankingTotal === 1 ? "" : "s" }}
            </span>
          </div>
          <template v-if="selected">
            <div class="sm_list custom_scroll">
              <div
                v-for="r in ranking"
                :key="r.google_id"
                class="sm_rank"
                :class="{ sm_rank_winner: rankPrize(r) }"
                :style="rankColor(r) ? { '--wc': rankColor(r) } : {}"
              >
                <div class="sm_rank_pos">{{ r.position }}</div>
                <img
                  v-if="hasPhoto(r.image_url) && !brokenAvatars[r.google_id]"
                  class="sm_rank_avatar"
                  :src="r.image_url"
                  referrerpolicy="no-referrer"
                  @error="onAvatarError(r)"
                />
                <div v-else class="sm_rank_avatar sm_rank_ph">{{ initial(r) }}</div>
                <div class="sm_rank_id">
                  <div class="sm_rank_name">{{ r.username || r.google_id }}</div>
                  <div class="sm_rank_country" v-if="r.country_code || r.online">
                    <span v-if="r.country_code">{{ r.country_code }} {{ countryFlag(r.country_code) }}</span>
                    <span v-if="r.online" class="sm_rank_online">online</span>
                  </div>
                  <div class="sm_rank_won" v-if="wonPrize(r)">
                    <b-icon-trophy-fill class="sm_rank_won_ico" />
                    <span>{{ wonPrize(r).prize }}</span>
                  </div>
                </div>
                <div v-if="isSubBadge(r.sub)" class="sm_sub" :class="subCls(r.sub)">
                  <b-icon-shield-fill v-if="r.sub === 'remover_ad_ano'" class="sm_crown shield_ic" />
                  <b-icon-star-fill v-else-if="r.sub === 'sub_pro'" class="sm_crown" />
                  <svg v-else class="sm_crown" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M5 17 L3 7 L8.5 11 L12 5 L15.5 11 L21 7 L19 17 Z M5 19 H19 V21 H5 Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                <div class="sm_rank_time">{{ fmtMs(r.time_ms) }}</div>
              </div>
              <div v-if="!ranking.length && !loadingRanking" class="sm_empty">sem jogadores ainda</div>
            </div>
          </template>
          <div v-else class="sm_empty sm_center">selecione uma temporada</div>
        </div>

        <!-- COL 4: regions (how prizes show) + gift-card stock -->
        <div class="sm_col sm_col_regions">
          <div class="sm_col_title">Regiões &amp; Prêmios</div>
          <div class="sm_list custom_scroll">
            <div
              v-for="c in supportedSettings"
              :key="c.country_code"
              class="sm_region"
              :class="{
                sm_region_has_gift: [
                  'google_play_gift_card',
                  'paypal_credit_gift_card',
                ].includes(c.product),
              }"
            >
              <div class="sm_region_head">
                <div>
                  <span class="sm_region_cc">{{ c.country_code }} {{getCountryEmoji(c.country_code)}}</span>
                </div>
              </div>
              <div class="sm_region_product">
                <span class="sm_region_prod" :class="'med_' + c.mediator">
                  {{ productLabel(c.product) }}
                </span>
                <div
                  v-if="c.product === 'google_play_gift_card'"
                  class="sm_google_gift_card"
                  aria-label="Google Play gift card"
                >
                  <svg class="sm_google_play_icon" viewBox="0 0 16 16" aria-hidden="true">
                    <path
                      fill="#fbbc04"
                      d="M14.222 9.374c1.037-.61 1.037-2.137 0-2.748L11.528 5.04 8.32 8l3.207 2.96 2.694-1.586Z"
                    />
                    <path
                      fill="#ea4335"
                      d="M10.627 11.49 7.583 8.68 1.03 14.73c.201 1.029 1.36 1.61 2.303 1.055l7.294-4.295Z"
                    />
                    <path fill="#4285f4" d="M1 13.396V2.603L6.846 8 1 13.396Z" />
                    <path
                      fill="#34a853"
                      d="m1.03 1.27 6.553 6.05 3.044-2.81L3.333.215C2.39-.341 1.231.24 1.03 1.27Z"
                    />
                  </svg>
                  <span>gift card</span>
                </div>
                <div
                  v-else-if="c.product === 'paypal_credit_gift_card'"
                  class="sm_paypal_gift_card"
                  aria-label="PayPal gift card"
                >
                  <div class="sm_paypal_brand">
                    <b-icon-paypal class="sm_paypal_icon" />
                    <span class="sm_paypal_word">PayPal</span>
                  </div>
                  <span class="sm_paypal_label">gift card</span>
                </div>
              </div>
              <div class="sm_region_vals">
                <span>$5 → <b>{{ sym(c.currency) }} {{ c["5_usd"] }}</b></span>
                <span>$10 → <b>{{ sym(c.currency) }} {{ c["10_usd"] }}</b></span>
              </div>
              <div class="sm_region_foot">
                <span class="sm_region_med">via {{ c.mediator }}</span>
                <span v-if="c.mediator === 'db'" class="sm_region_stock">
                  estoque:
                  <b>{{ stockCount(c.country_code, "5_usd") }}</b> de $5
                  <span class="sm_region_stock_sep">—</span>
                  <b>{{ stockCount(c.country_code, "10_usd") }}</b> de $10
                </span>
              </div>
            </div>
            <div v-if="!supportedSettings.length && !loadingRegions" class="sm_empty">
              sem config
            </div>
          </div>
          <div class="sm_form">
            <div class="sm_form_title">Adicionar código (estoque)</div>
            <select class="sm_input sm_dt" v-model="stockForm.country_code">
              <option v-for="cc in dbCountries" :key="cc" :value="cc">
                {{ cc }} — {{ getCountryName(cc) }}
              </option>
            </select>
            <div class="sm_seg sm_seg_full">
              <button
                type="button"
                class="sm_seg_btn"
                :class="{ active: stockForm.value_usd === '5_usd' }"
                @click="stockForm.value_usd = '5_usd'"
              >
                $5
              </button>
              <button
                type="button"
                class="sm_seg_btn"
                :class="{ active: stockForm.value_usd === '10_usd' }"
                @click="stockForm.value_usd = '10_usd'"
              >
                $10
              </button>
            </div>
            <input
              class="sm_input"
              v-model="stockForm.value_currency"
              placeholder="Valor local — ex.: 5000 IQD"
            />
            <textarea
              class="sm_input sm_codes custom_scroll"
              v-model="stockForm.code"
              placeholder="Código(s) — um por linha"
            ></textarea>
            <CustomButton
              :name="'Adicionar código(s)'"
              :loading="savingStock"
              @ontap="saveStockCode"
            />
          </div>
        </div>
      </div>

      <div class="sm_msg" v-if="msg">{{ msg }}</div>
    </div>
  </div>
</template>

<script>
import settings from "../settings";
import utils from "../utils";
import CustomButton from "@/components/CustomButton.vue";

export default {
  name: "SeasonManager",
  components: { CustomButton },
  props: {
    visible: { type: Boolean, default: false },
  },
  data() {
    return {
      internalVisible: false,
      seasons: [],
      selected: null,
      prizes: [],
      ranking: [],
      rankingTotal: 0, // total users competing (may exceed ranking.length if limit < total)
      // google_id -> true once its image_url 404s/fails to load, so the
      // placeholder letter-avatar takes over instead of a broken-image icon.
      brokenAvatars: {},
      redelivering: null, // position currently being retried, or null
      newSeason: { title: "", start_at: "", end_at: "" },
      prizeForm: {
        position: null,
        prize: "",
        color: "#29ff7e",
        type: "gift_card",
        value: "5_usd",
      },
      loadingSeasons: false,
      loadingPrizes: false,
      loadingRanking: false,
      rankingPollTimer: null,
      creating: false,
      savingPrize: false,
      finalizing: false,
      msg: "",
      // COL 4: region config + gift-card stock
      rewardsSettings: [],
      giftStock: [],
      loadingRegions: false,
      savingStock: false,
      stockForm: {
        country_code: "IQ",
        value_usd: "5_usd",
        value_currency: "",
        code: "",
      },
      flags:[
        {country_code:"BR", emoji:"🇧🇷", name:"Brasil"},
        {country_code:"MX", emoji:"🇲🇽", name:"México"},
        {country_code:"IT", emoji:"🇮🇹", name:"Itália"},
        {country_code:"CA", emoji:"🇨🇦", name:"Canadá"},
        {country_code:"SA", emoji:"🇸🇦", name:"Arábia Saudita"},
        {country_code:"AE", emoji:"🇦🇪", name:"Emirados Árabes Unidos"},
        {country_code:"GB", emoji:"🇬🇧", name:"Reino Unido"},
        {country_code:"US", emoji:"🇺🇸", name:"Estados Unidos"},
        {country_code:"CL", emoji:"🇨🇱", name:"Chile"},
        {country_code:"IQ", emoji:"🇮🇶", name:"Iraque"},
        {country_code:"IN", emoji:"🇮🇳", name:"Índia"},
        {country_code:"ID", emoji:"🇮🇩", name:"Indonésia"},
        {country_code:"OTHERS", emoji:"🌍", name:"Outros"}
      ],
    };
  },
  created() {
    this.internalVisible = this.visible;
    if (this.visible) {
      this.loadSeasons();
      this.loadRegions();
      this.startRankingPoll();
    }
  },
  unmounted() {
    this.stopRankingPoll();
  },
  watch: {
    visible(val) {
      this.internalVisible = val;
      if (val) {
        this.loadSeasons();
        this.loadRegions();
        this.startRankingPoll();
      } else {
        this.stopRankingPoll();
      }
    },
  },
  computed: {
    // Countries actually offered — support:false falls back to OTHERS/PayPal
    // and shouldn't be listed as its own region in the panel.
    supportedSettings() {
      return this.rewardsSettings.filter((c) => c.support !== false);
    },
    // db-mediated, supported countries — the only ones that take stock codes.
    dbCountries() {
      return this.supportedSettings
        .filter((c) => c.mediator === "db")
        .map((c) => c.country_code);
    },
  },
  methods: {
    // Panel JWT (cookie) — sent in the body on POST, in the `token` header on GET.
    token() {
      return utils.getCookie(settings.cookieAuthToken);
    },
    authHeader() {
      return { headers: { token: this.token() } };
    },
    flash(m) {
      this.msg = m;
      setTimeout(() => {
        if (this.msg === m) this.msg = "";
      }, 3500);
    },
    close() {
      this.$emit("onclose");
      this.internalVisible = false;
    },
    getCountryEmoji(countryCode) {
      const country = this.flags.find(
        item => item.country_code === countryCode
      );

      return country ? country.emoji : "🌍";
    },
    getCountryName(countryCode) {
      const country = this.flags.find(
        item => item.country_code === countryCode
      );

      return country ? country.name : countryCode;
    },
    async loadSeasons() {
      this.loadingSeasons = true;
      try {
        let res = await this.axios.get(settings.APIRoutes.getSeasons(), this.authHeader());
        this.seasons = res.data.seasons || [];
        // Keep the current selection if it's still there; otherwise auto-select
        // the most recent season (list is ordered newest-first) so prizes/ranking
        // already show when the dialog opens.
        const current =
          this.selected && this.seasons.find((x) => x.id === this.selected.id);
        if (current) {
          this.selected = current;
        } else if (this.seasons.length) {
          this.selectSeason(this.seasons[0]);
        }
      } catch (e) {
        this.flash("Erro ao carregar temporadas");
      }
      this.loadingSeasons = false;
    },
    async selectSeason(s) {
      this.selected = s;
      await Promise.all([this.loadPrizes(s.id), this.loadRanking(s.id)]);
    },
    async loadPrizes(id) {
      this.loadingPrizes = true;
      try {
        let res = await this.axios.get(settings.APIRoutes.seasonPrizes(id), this.authHeader());
        this.prizes = res.data.prizes || [];
      } catch (e) {
        this.flash("Erro ao carregar prêmios");
      }
      this.loadingPrizes = false;
    },
    async loadRanking(id) {
      this.loadingRanking = true;
      try {
        let res = await this.axios.get(settings.APIRoutes.seasonRanking(id), this.authHeader());
        this.ranking = res.data.ranking || [];
        this.rankingTotal = res.data.total || 0;
      } catch (e) {
        this.flash("Erro ao carregar ranking");
      }
      this.loadingRanking = false;
    },
    // Auto-refreshes the ranking every 10s while the dialog is open, so live
    // playtime updates show up without the user having to reselect the season.
    startRankingPoll() {
      this.stopRankingPoll();
      this.rankingPollTimer = setInterval(() => {
        if (this.selected && !this.loadingRanking) {
          this.loadRanking(this.selected.id);
        }
      }, 10000);
    },
    stopRankingPoll() {
      if (this.rankingPollTimer) {
        clearInterval(this.rankingPollTimer);
        this.rankingPollTimer = null;
      }
    },
    // ---- COL 4: regions config + gift-card stock ----
    async loadRegions() {
      this.loadingRegions = true;
      try {
        const [cfg, stock] = await Promise.all([
          this.axios.get(settings.APIRoutes.rewardsSettings(), this.authHeader()),
          this.axios.get(
            settings.APIRoutes.giftCardStockSummary(),
            this.authHeader(),
          ),
        ]);
        this.rewardsSettings = cfg.data.settings || [];
        this.giftStock = stock.data.stock || [];
      } catch (e) {
        this.flash("Erro ao carregar regiões");
      }
      this.loadingRegions = false;
    },
    async saveStockCode() {
      const f = this.stockForm;
      if (!f.country_code || !f.value_usd || !f.code.trim()) {
        this.flash("País, valor e código são obrigatórios");
        return;
      }
      this.savingStock = true;
      try {
        const res = await this.axios.post(settings.APIRoutes.addGiftCardStock(), {
          token: this.token(),
          country_code: f.country_code,
          value_usd: f.value_usd,
          value_currency: f.value_currency || null,
          code: f.code,
        });
        this.flash(`${res.data.added} código(s) adicionado(s)`);
        this.stockForm.code = "";
        await this.loadRegions();
      } catch (e) {
        this.flash("Erro ao adicionar código");
      }
      this.savingStock = false;
    },
    // Currency symbol for the region list (fallback: code + space).
    sym(currency) {
      const m = {
        USD: "$",
        BRL: "R$",
        EUR: "€",
        GBP: "£",
        CAD: "C$",
        SAR: "SAR ",
        AED: "AED ",
        MXN: "MX$",
        IQD: "IQD ",
        INR: "₹",
        IDR: "Rp",
      };
      return m[currency] || currency + " ";
    },
    productLabel(product) {
      if (product === "google_play_gift_card") return "Google Play";
      if (product === "paypal_credit_gift_card") return "PayPal";
      return product;
    },
    // Available (undelivered) codes in stock for a country + denomination.
    stockCount(cc, valueUsd) {
      const row = this.giftStock.find(
        (s) => s.country_code === cc && s.value_usd === valueUsd,
      );
      return row ? row.available : 0;
    },
    // datetime-local gives "YYYY-MM-DDTHH:mm[:ss]"; the API wants "YYYY-MM-DD HH:mm:ss".
    dtToApi(v) {
      if (!v) return null;
      let s = v.replace("T", " ");
      if (s.length === 16) s += ":00"; // no seconds selected
      return s;
    },
    async createSeason() {
      const endAt = this.dtToApi(this.newSeason.end_at);
      const startAt = this.dtToApi(this.newSeason.start_at);
      if (!this.newSeason.title || !endAt) {
        this.flash("Título e fim são obrigatórios");
        return;
      }
      this.creating = true;
      try {
        await this.axios.post(settings.APIRoutes.createSeason(), {
          token: this.token(),
          title: this.newSeason.title,
          end_at: endAt,
          start_at: startAt,
        });
        this.newSeason = { title: "", start_at: "", end_at: "" };
        this.flash("Temporada criada");
        await this.loadSeasons();
      } catch (e) {
        this.flash("Erro ao criar temporada");
      }
      this.creating = false;
    },
    // Display label for a subscription tier enum value (panel + auto prize name).
    subPrizeLabel(value) {
      return (
        { premium_sub: "PREMIUM SUB", plus_sub: "PLUS SUB", pro_sub: "PRO SUB" }[
          value
        ] || "PREMIUM SUB"
      );
    },
    // Switches the prize form's type, defaulting `value` to something valid
    // for that type (it may still hold a value from the other type).
    setPrizeType(type) {
      this.prizeForm.type = type;
      if (type === "gift_card") {
        if (!["5_usd", "10_usd"].includes(this.prizeForm.value)) {
          this.prizeForm.value = "5_usd";
        }
      } else if (!["premium_sub", "plus_sub", "pro_sub"].includes(this.prizeForm.value)) {
        this.prizeForm.value = "premium_sub";
      }
    },
    editPrize(p) {
      // Only gift_card / subscription now; anything else (legacy "other", null)
      // edits as a subscription so its name/content stay editable.
      const type = p.type === "gift_card" ? "gift_card" : "subscription";
      const value =
        type === "gift_card"
          ? p.value === "10_usd"
            ? "10_usd"
            : "5_usd"
          : ["premium_sub", "plus_sub", "pro_sub"].includes(p.value)
            ? p.value
            : "premium_sub";
      this.prizeForm = {
        position: p.position,
        prize: p.prize,
        color: p.color || "#29ff7e",
        type,
        value,
      };
    },
    async savePrize() {
      if (!this.selected) return;
      const f = this.prizeForm;
      if (!f.position || f.position < 1) {
        this.flash("Posição (>= 1) é obrigatória");
        return;
      }
      // Gift card: name is built from the denomination (the app rebuilds it per
      // country). Subscription: name is built from the selected tier. Neither
      // needs a manual prize-name input anymore. `content` isn't set here for
      // either type — gift_card fills it on delivery, subscription gets its
      // redemption code generated in subscription_codes on finalize (shown
      // read-only via p.subscription_code, see the prize list above).
      let prize, value;
      if (f.type === "gift_card") {
        value = f.value === "10_usd" ? "10_usd" : "5_usd";
        prize = value === "10_usd" ? "Gift Card $10" : "Gift Card $5";
      } else {
        value = ["premium_sub", "plus_sub", "pro_sub"].includes(f.value)
          ? f.value
          : "premium_sub";
        prize = this.subPrizeLabel(value);
      }
      this.savingPrize = true;
      try {
        await this.axios.post(settings.APIRoutes.saveSeasonPrize(this.selected.id), {
          token: this.token(),
          position: f.position,
          prize,
          content: null,
          color: f.color || null,
          type: f.type,
          value,
        });
        this.prizeForm = {
          position: null,
          prize: "",
          color: "#29ff7e",
          type: "gift_card",
          value: "5_usd",
        };
        this.flash("Prêmio salvo");
        await this.loadPrizes(this.selected.id);
      } catch (e) {
        this.flash("Erro ao salvar prêmio");
      }
      this.savingPrize = false;
    },
    async finalize() {
      if (!this.selected || this.selected.status === "ended") return;
      if (!confirm("Finalizar a temporada agora e atribuir os vencedores?")) return;
      this.finalizing = true;
      try {
        await this.axios.post(settings.APIRoutes.finalizeSeason(this.selected.id), {
          token: this.token(),
        });
        this.flash("Temporada finalizada");
        await this.loadSeasons();
        await this.loadPrizes(this.selected.id);
      } catch (e) {
        this.flash("Erro ao finalizar");
      }
      this.finalizing = false;
    },
    // Retries delivery of one gift_card prize (Reloadly order / DB stock code).
    // A no-op server-side if it's already delivered — safe to click again.
    async redeliverPrize(p) {
      if (!this.selected || this.redelivering) return;
      this.redelivering = p.position;
      try {
        const res = await this.axios.post(
          settings.APIRoutes.redeliverPrize(this.selected.id, p.position),
          { token: this.token() },
        );
        this.flash(
          res.data.delivery_status === "delivered"
            ? `Prêmio #${p.position} entregue`
            : `Falha ao entregar #${p.position}: ${res.data.error || "erro desconhecido"}`,
        );
        await this.loadPrizes(this.selected.id);
      } catch (e) {
        this.flash("Erro ao reentregar prêmio");
      }
      this.redelivering = null;
    },
    // ---- formatting helpers ----
    statusLabel(s) {
      if (s.status === "ended") return "encerrada";
      if (s.start_at && new Date(s.start_at) > new Date()) return "agendada";
      if (new Date(s.end_at) <= new Date()) return "expirando";
      return "ativa";
    },
    statusClass(s) {
      return "st_" + this.statusLabel(s);
    },
    endLabel(s) {
      return s.status === "ended" ? "encerrou em" : "termina em";
    },
    fmtDate(d) {
      if (!d) return "—";
      return new Date(d).toLocaleString();
    },
    fmtMs(ms) {
      ms = Number(ms) || 0;
      const total = Math.floor(ms / 1000);
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      if (h > 0) return `${h}h${m}m`; // with hours, hide the seconds
      if (m > 0) return `${m}m${s}s`;
      return `${s}s`;
    },
    hasPhoto(url) {
      return typeof url === "string" && /^https?:\/\//i.test(url);
    },
    // hasPhoto only checks the URL shape — Google photo links can still 404 /
    // expire. This catches the actual load failure so the <img v-if> falls
    // back to the letter-avatar instead of showing a broken-image icon.
    onAvatarError(r) {
      this.brokenAvatars[r.google_id] = true;
    },
    initial(r) {
      return (r.username || r.google_id || "?").trim().charAt(0).toUpperCase();
    },
    // ISO-2 country code -> its flag emoji (regional indicator symbols).
    countryFlag(code) {
      return String(code)
        .toUpperCase()
        .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
    },
    // The full prize object this ranked user won in the selected season, or null.
    wonPrize(r) {
      return (
        this.prizes.find(
          (x) => x.winner_google_id && x.winner_google_id === r.google_id,
        ) || null
      );
    },
    // The prize tied to this ranking row: the one actually assigned to the user
    // (finalized seasons) or, live, the prize defined for their current ranking
    // position — so the position colors show during an active season too.
    rankPrize(r) {
      return (
        this.prizes.find(
          (x) => x.winner_google_id && x.winner_google_id === r.google_id,
        ) ||
        this.prizes.find((x) => Number(x.position) === Number(r.position)) ||
        null
      );
    },
    // The theme color of this row's prize (or null).
    rankColor(r) {
      const p = this.rankPrize(r);
      return p ? p.color || null : null;
    },
    isSubBadge(sub) {
      return ["remover_ad_ano", "sub_pro", "sub_premium"].includes(sub);
    },
    // remover_ad_ano = plus (shield/bronze), sub_pro = star (silver),
    // sub_premium = crown (yellow).
    subCls(sub) {
      if (sub === "remover_ad_ano") return "bplus";
      if (sub === "sub_pro") return "bpro";
      if (sub === "sub_premium") return "bpremium";
      return "";
    },
  },
};
</script>

<style scoped>
.sm_overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sm_box {
  width: min(1440px, 96vw);
  height: 890px;
  background: #212121;
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  color: var(--textColor, #eee);
  box-sizing: border-box;
}
.sm_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 34px;
}
.sm_title {
  font-size: 18px;
  font-weight: bold;
}
.sm_close {
  cursor: pointer;
  padding: 4px 6px;
  background-color: #ff3030;
  border-radius: 50%;
}
.close_icon{
  position: relative;
  top: 1px;
  color: #1d1d1d;
}
.sm_region_med{
  font-size: 10px;
  color: #0bfdff;
}
.sm_body {
  flex: 1;
  display: flex;
  gap: 12px;
  margin-top: 10px;
  min-height: 0;
}
.sm_col {
  background: #2b2b2b;
  border-radius: 10px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.sm_col_seasons {
  flex: 0 0 280px;
}
.sm_col_prizes {
  flex: 1 1 auto;
}
.sm_col_ranking {
  flex: 0 0 350px;
}
.sm_col_regions {
  flex: 0 0 320px;
}
.sm_region {
  background: #303030;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  border-radius: 10px;
  padding: 0px 9px;
  margin-left: 10px;
  margin-right: 6px;
  position: relative;
}
.sm_region_has_gift {
  padding-right: 82px;
}
.sm_region_head {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  top: 4px;
}
.sm_region_cc {
  font-weight: bold;
  font-size: 13px;
  background: rgba(255, 255, 255, 0.07);
  border-radius: 6px;
  padding: 2px 8px;
  letter-spacing: 0.5px;
}
.sm_region_prod {
  display: block;
  font-size: 10px;
  font-weight: bold;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
}
.sm_region_product {
  position: absolute;
  width: 64px;
  top: 4px;
  right: 11px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.sm_google_gift_card {
  width: 64px;
  height: 40px;
  margin-top: 5px;
  border-radius: 7px;
  background: #fff;
  color: #383838;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.28);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}
.sm_google_play_icon {
  width: 21px;
  height: 21px;
  display: block;
}
.sm_google_gift_card span {
  margin-top: 2px;
  color: #454545;
  font-size: 9px;
  font-weight: bold;
  line-height: 1;
  text-transform: lowercase;
}
.sm_paypal_gift_card {
  width: 64px;
  height: 40px;
  margin-top: 5px;
  border-radius: 7px;
  background: linear-gradient(145deg, #1d427d 0%, #287bc0 100%);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.28);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  overflow: hidden;
}
.sm_paypal_brand {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
}
.sm_paypal_icon {
  width: 14px;
  height: 14px;
  color: #16b9ee;
  filter: drop-shadow(-1px 0 0 #123879);
}
.sm_paypal_word {
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  font-style: italic;
  line-height: 1;
  letter-spacing: -0.7px;
}
.sm_paypal_label {
  margin-top: 3px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 9px;
  font-weight: bold;
  line-height: 1;
  text-transform: lowercase;
}
.med_reloadly {
  color: #29ff7e;
  background: rgba(41, 255, 126, 0.12);
}
.med_db {
  color: #f0c04a;
  background: rgba(240, 192, 74, 0.15);
}
.sm_region_vals {
  display: flex;
  gap: 12px;
  font-size: 11.5px;
  opacity: 0.85;
  margin-top: 8.5px;
}
.sm_region_vals b {
  color: var(--textColor, #e4e4e4);
}
.sm_region_foot {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 10px;
  opacity: 0.6;
  margin-top: 4px;
  padding-bottom: 5px;
}
.sm_region_stock {
  color: #f0c04a;
  font-size: 10.5px;
  opacity: 1;
  white-space: nowrap;
}
.sm_region_stock b {
  color: #ffd86b;
  font-weight: 800;
}
.sm_region_stock_sep {
  margin: 0 3px;
}
.sm_seg_full {
  width: 100%;
  margin-bottom: 8px;
}
.sm_seg_full .sm_seg_btn {
  flex: 1;
  justify-content: center;
  font-size: 12px;
  padding: 5px;
  font-weight: bold;
}
.sm_codes {
  min-height: 68px;
  resize: vertical;
  font-family: monospace;
  line-height: 1.5;
}
.sm_col_title {
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 8px;
  color: var(--theme, #2ee681);
}
.sm_col_sub {
  color: var(--textColor, #eee);
  font-weight: normal;
  font-size: 12px;
  opacity: 0.7;
}
.sm_col_title_row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}
.sm_col_count {
  color: var(--textColor, #eee);
  font-weight: normal;
  font-size: 13px;
  opacity: 0.6;
  white-space: nowrap;
}
.sm_list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 10px;
  padding-top: 5px;
}
.sm_season {
  background: #303030;
  border: 3px solid #3d3d3d;
  border-radius: 10px;
  padding: 10px 12px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.sm_season:hover {
  border-color: #555;
}
.sm_season.active {
  border-color: var(--theme, #29ff7e);
  background: rgba(41, 255, 126, 0.08);
}
.sm_season_head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.sm_season_title {
  font-size: 14px;
  font-weight: bold;
  color: var(--textColor, #e4e4e4);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sm_season_sub {
  font-size: 11px;
  opacity: 0.7;
  margin-top: 6px;
  display: flex;
  gap: 5px;
  align-items: center;
}
.sm_season_ico {
  font-size: 11px;
  position: relative;
  top: 0.5px;
}
.sm_badge {
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 9px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  flex-shrink: 0;
}
.st_ativa {
  color: var(--theme, #29ff7e);
  background: rgba(41, 255, 126, 0.14);
}
.st_agendada {
  color: #f0c04a;
  background: rgba(240, 192, 74, 0.15);
}
.st_encerrada {
  color: #9a9a9a;
  background: rgba(255, 255, 255, 0.08);
}
.st_expirando {
  color: #f0894a;
  background: rgba(240, 137, 74, 0.15);
}
.sm_form {
  border-top: 1px solid #3a3a3a;
  margin-top: 8px;
  padding-top: 8px;
}
.sm_form_title {
  font-size: 12px;
  opacity: 0.8;
  margin-bottom: 6px;
}
.sm_input {
  width: 100%;
  box-sizing: border-box;
  background: var(--background0, #1d1d1d);
  border: 1px solid #3a3a3a;
  border-radius: 8px;
  color: var(--textColor, #e4e4e4);
  padding: 9px 12px;
  font-size: 12.5px;
  margin-bottom: 8px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background 0.15s ease;
}
.sm_input::placeholder {
  color: #7c7c7c;
}
.sm_input:hover {
  border-color: #4c4c4c;
}
.sm_input:focus {
  border-color: var(--theme, #29ff7e);
  background: #202020;
  box-shadow: 0 0 0 3px rgba(41, 255, 126, 0.12);
}
.sm_field_label {
  display: block;
  font-size: 10px;
  opacity: 0.65;
  margin-bottom: 3px;
}
.sm_dt {
  color-scheme: dark;
}
.sm_content_row {
  display: flex;
  gap: 8px;
  align-items: stretch;
  margin-bottom: 8px;
}
.sm_content_input {
  flex: 1;
  margin-bottom: 0;
}
.sm_seg {
  display: flex;
  background: var(--background0, #1d1d1d);
  border: 1px solid #3a3a3a;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}
.sm_seg_btn {
  border: none;
  background: transparent;
  color: #8a8a8a;
  padding: 0 9px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background 0.15s ease, color 0.15s ease;
}
.sm_seg_btn:not(:last-child) {
  border-right: 1px solid #3a3a3a;
}
.sm_seg_btn:hover {
  color: #ccc;
}
.sm_seg_btn.active {
  background: rgba(41, 255, 126, 0.14);
  color: var(--theme, #29ff7e);
}
.sm_color_row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.sm_color_label {
  font-size: 11px;
  opacity: 0.7;
}
.sm_color {
  width: 34px;
  height: 26px;
  padding: 0;
  border: 1px solid #3a3a3a;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}
.sm_color::-webkit-color-swatch-wrapper {
  padding: 2px;
}
.sm_color::-webkit-color-swatch {
  border: none;
  border-radius: 4px;
}
.sm_color_hex_input {
  flex: 1;
  margin-bottom: 0;
  padding: 6px 10px;
  font-family: monospace;
  text-transform: uppercase;
}
.sm_prize {
  background: #303030;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  border-radius: 10px;
  padding: 9px 11px;
  display: flex;
  gap: 10px;
  align-items: center;
  margin-right: 6px;
}
.sm_prize_bar {
  width: 4px;
  align-self: stretch;
  border-radius: 4px;
  background: var(--pc, var(--theme, #29ff7e));
  flex-shrink: 0;
}
.sm_prize_pos {
  font-weight: bold;
  font-size: 12px;
  color: var(--pc, var(--theme, #29ff7e));
  background: rgba(255, 255, 255, 0.07);
  border-radius: 8px;
  padding: 4px 0;
  min-width: 30px;
  text-align: center;
  flex-shrink: 0;
}
.sm_prize_info {
  flex: 1;
  min-width: 0;
}
.sm_prize_name {
  font-size: 13.5px;
  font-weight: bold;
  color: var(--pc, var(--textColor, #e4e4e4));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sm_prize_content {
  font-size: 11px;
  opacity: 0.85;
  margin-top: 3px;
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
}
.sm_prize_content span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sm_prize_ico {
  color: #f0c04a;
  font-size: 11px;
  flex-shrink: 0;
}
.sm_delivery {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  font-weight: bold;
  margin-top: 5px;
}
.dlv_delivered {
  color: var(--theme, #29ff7e);
}
.dlv_failed {
  color: #ff5a5a;
}
.dlv_none {
  color: #f0c04a;
  opacity: 0.85;
}
.sm_redeliver_btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
  background: rgba(255, 90, 90, 0.12);
  border: 1px solid rgba(255, 90, 90, 0.35);
  color: #ff5a5a;
  border-radius: 12px;
  padding: 2px 9px;
  font-size: 10px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.15s ease;
}
.sm_redeliver_btn:hover:not(:disabled) {
  background: rgba(255, 90, 90, 0.22);
}
.sm_redeliver_btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.spin {
  animation: sm_spin 0.9s linear infinite;
}
@keyframes sm_spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
.sm_prize_edit {
  cursor: pointer;
  opacity: 0.55;
  flex-shrink: 0;
  transition: opacity 0.15s ease, color 0.15s ease;
}
.sm_prize_edit:hover {
  opacity: 1;
  color: var(--theme, #29ff7e);
}
.sm_finalize {
  margin-top: 8px;
}
.sm_rank {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #333;
  border: 3px solid transparent;
  border-radius: 12px;
  padding: 5px 8px;
  min-height: 40px;
}
.sm_rank_winner {
  border-color: var(--wc, var(--theme, #29ff7e));
  box-shadow: 0 0 10px -3px var(--wc, var(--theme, #29ff7e));
}
.sm_rank:not(.sm_rank_winner) {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
}
.sm_rank_pos {
  font-weight: bold;
  min-width: 18px;
  text-align: center;
  color: var(--wc, var(--theme, #2ee681));
}
.sm_rank_avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: #444;
}
.sm_rank_ph {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: bold;
  color: #ccc;
}
.sm_rank_id {
  flex: 1;
  min-width: 0;
}
.sm_rank_name {
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sm_rank_country {
  font-size: 11px;
  opacity: 0.8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 1px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.sm_rank_online {
  color: #29ff7e;
  opacity: 1;
  font-weight: bold;
}
.sm_rank_won {
  font-size: 12px;
  color: var(--wc, #f0c04a);
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.sm_rank_won span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: bold;
}
.sm_rank_won_ico {
  font-size: 12px;
  flex-shrink: 0;
}
.sm_sub {
  width: 25px;
  height: 25px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 2px solid;
  font-size: 10px;
  flex-shrink: 0;
}
.sm_crown {
  width: 19px;
  height: 19px;
  position: relative;
  top: -1px;

}
.shield_ic{
  width: 17px;
  height: 17px;
  top:0.5px
}
.bplus {
  color: #cd7f32;
  border-color: #cd7f32;
  position: relative;
}
.bpro {
  color: #cfcfd6;
  border-color: #cfcfd6;
}
.bpremium {
  color: #f2c21a;
  border-color: #f2c21a;
}
.sm_rank_time {
  font-size: 12px;
  font-weight: bold;
  opacity: 0.85;
  white-space: nowrap;
  width: 55px;
  text-align: right;
  color: var(--wc, var(--textColor, #e4e4e4));
}
.sm_empty {
  opacity: 0.5;
  font-size: 12px;
  padding: 8px;
}
.sm_center {
  text-align: center;
  margin: auto;
}
.sm_msg {
  margin-top: 8px;
  font-size: 12px;
  color: var(--theme, #2ee681);
  text-align: center;
}
</style>
